import React, { useState, useEffect } from 'react';
import { useAuth } from '@/_core/hooks/useAuth';
import { trpc } from '@/lib/trpc';
import Onboarding from './Onboarding';
import WorkoutCalendar from '@/components/WorkoutCalendar';
import { RecoveryStatusWidget } from '@/components/RecoveryStatusWidget';
import WorkoutBuilder from '@/components/WorkoutBuilder';
import LiveSessionTracker from '@/components/LiveSessionTracker';
import WeightTracker from '@/components/WeightTracker';
import BodyMeasurementsTracker from '@/components/BodyMeasurementsTracker';
import PRsAndTemplates from '@/components/PRsAndTemplates';
import StretchingTracker from '@/components/StretchingTracker';
import RecoverTab from '@/components/RecoverTab';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Crosshair, Hammer, PersonStanding, LayoutGrid, Gauge, Download, Upload, Trash2, LogIn, Loader2, Activity, Users, Flame, Crown, Eye, X, Share2, AlertTriangle } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import TrainerDashboard from './TrainerDashboard';
import TrainerRoster from '@/components/TrainerRoster';
import UserProfile from './UserProfile';
import NutritionTracker from '@/components/NutritionTracker';
import { Link } from 'wouter';
import { useClientContext } from '@/contexts/ClientContext';
import { TelemetryInsightBanner } from '@/components/TelemetryInsightCard';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getLoginUrl } from '@/const';

// Types for live session (matches server schema)
interface WorkoutSetData {
  id: string;
  reps?: number;
  weight?: number;
  completed: boolean;
  isPR?: boolean;
  isWarmup?: boolean;
  durationSeconds?: number;
  durationMinutes?: number;
  distanceMiles?: number;
}

interface PlannedExerciseData {
  id: string;
  exerciseId: string;
  sets: WorkoutSetData[];
  notes?: string;
}

interface LiveSessionData {
  id: string;
  date: string;
  name: string;
  notes?: string;
  isLive?: boolean;
  completedAt?: number | null;
  plannedExercises: PlannedExerciseData[];
}

/** Small wrapper that fetches insights and renders the banner — avoids prop drilling */
function TelemetryInsightBannerWrapper({ compact }: { compact?: boolean }) {
  const { data: insights, isLoading } = trpc.telemetry.getInsights.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
    retry: false,
  });
  if (isLoading || !insights || insights.length === 0) return null;
  return <TelemetryInsightBanner insights={insights as any} compact={compact} />;
}

export default function Home() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { isPaid } = useSubscription();
  const { activeClient, isViewingClient, exitClientView } = useClientContext();
  const utils = trpc.useUtils();

  // Onboarding gate — only query after auth is confirmed
  const { data: profile, isLoading: profileLoading } = trpc.profile.get.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const showOnboarding = isAuthenticated && !profileLoading && !profile?.onboardingCompleted;

  const handleOnboardingComplete = () => {
    utils.profile.get.invalidate();
  };

  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [liveSession, setLiveSession] = useState<LiveSessionData | null>(null);
  const [activeTab, setActiveTab] = useState<'plan' | 'weight' | 'prs' | 'mobility' | 'calendar' | 'profile' | 'clients' | 'fuel'>('plan');
  const [forceNewSession, setForceNewSession] = useState(false);

  // ── Session recovery: detect backed-up sessions from failed saves ──────────
  const [backedUpSessions, setBackedUpSessions] = useState<LiveSessionData[]>([]);
  const [retryingSave, setRetryingSave] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    const backed: LiveSessionData[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('session_backup_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '');
          if (data?.id) backed.push(data as LiveSessionData);
        } catch (_) {}
      }
    }
    setBackedUpSessions(backed);
  }, [isAuthenticated]);

  const handleRetryBackedUpSession = async (s: LiveSessionData) => {
    setRetryingSave(true);
    try {
      await saveSessionMutation.mutateAsync(s as any);
      localStorage.removeItem(`session_backup_${s.id}`);
      setBackedUpSessions(prev => prev.filter(b => b.id !== s.id));
      toast.success(`🔥 "${s.name}" saved successfully!`);
      triggerRefresh();
    } catch (err) {
      toast.error('Save failed again — check your connection and try once more.');
    } finally {
      setRetryingSave(false);
    }
  };

  const handleDismissBackup = (id: string) => {
    localStorage.removeItem(`session_backup_${id}`);
    setBackedUpSessions(prev => prev.filter(b => b.id !== id));
  };

  const handleAddNewWorkout = (date: string) => {
    setSelectedDate(date);
    setForceNewSession(true);
    setActiveTab('plan');
  };

  // Load sessions to check for active live session on mount
  const { data: sessions } = trpc.sessions.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const saveSessionMutation = trpc.sessions.save.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
      utils.prs.list.invalidate();
    },
  });

  const deleteSessionMutation = trpc.sessions.delete.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
      utils.prs.list.invalidate();
    },
  });

  // Check for active live session on mount
  useEffect(() => {
    if (sessions) {
      const active = sessions.find((s) => s.isLive);
      if (active) {
        setLiveSession({
          ...active,
          notes: active.notes ?? undefined,
          completedAt: active.completedAt ?? undefined,
          plannedExercises: active.plannedExercises as PlannedExerciseData[],
        });
      }
    }
  }, [sessions]);

  const triggerRefresh = () => {
    utils.sessions.list.invalidate();
    utils.sessions.byDate.invalidate();
    utils.prs.list.invalidate();
    utils.weight.list.invalidate();
    utils.measurements.list.invalidate();
  };

  const handleStartLive = async (session: LiveSessionData) => {
    const live = { ...session, isLive: true };
    await saveSessionMutation.mutateAsync(live);
    setLiveSession(live);
    toast.success(`Live session started: "${session.name}"`);
  };

  const handleLiveComplete = async () => {
    // The LiveSessionTracker already awaited the final save (with completedAt + isLive:false)
    // before calling onComplete. We just need to clear local state and refresh the list.
    setLiveSession(null);
    triggerRefresh();
    // Re-scan localStorage to pick up any cleared backup keys.
    // On a successful save, LiveSessionTracker calls localStorage.removeItem(BACKUP_KEY)
    // before calling onComplete — so re-scanning here will correctly show 0 backed-up sessions.
    const backed: LiveSessionData[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('session_backup_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '');
          if (data?.id) backed.push(data as LiveSessionData);
        } catch (_) {}
      }
    }
    setBackedUpSessions(backed);
  };

  const handleLiveCancel = async () => {
    if (liveSession) {
      await deleteSessionMutation.mutateAsync({ sessionId: liveSession.id });
    }
    setLiveSession(null);
    triggerRefresh();
  };

  // Export all data as JSON backup
  const handleExport = async () => {
    try {
      const [allSessions, allWeights, allMeasurements, allTemplates, allPRs, allSplits] = await Promise.all([
        utils.sessions.list.fetch(),
        utils.weight.list.fetch(),
        utils.measurements.list.fetch(),
        utils.templates.list.fetch(),
        utils.prs.list.fetch(),
        utils.splits.list.fetch(),
      ]);
      const data = {
        sessions: allSessions,
        weights: allWeights,
        measurements: allMeasurements,
        templates: allTemplates,
        prs: allPRs,
        splits: allSplits,
        exportedAt: new Date().toISOString(),
      };
      const dataStr = JSON.stringify(data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const link = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `forge_backup_${selectedDate}.json`);
      link.click();
      toast.success("Backup exported successfully!");
    } catch {
      toast.error("Failed to export backup.");
    }
  };

  // Show onboarding for first-time users
  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  // Show login screen if not authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-widest">LOADING TELEMETRY...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-16 w-16 bg-primary flex items-center justify-center rounded-sm rotate-3">
            <Hammer className="h-10 w-10 text-primary-foreground -rotate-3" />
          </div>
          <h1 className="font-mono text-2xl font-bold tracking-wider text-primary">FORGE // TELEMETRY</h1>
          <p className="font-mono text-xs text-muted-foreground uppercase tracking-widest">ROSS'S WORKOUT COMMAND CENTER</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs">Your data is now stored securely in the cloud. Sign in to access your workout logs.</p>
        </div>
        <Button
          onClick={() => { window.location.href = getLoginUrl(); }}
          className="font-mono uppercase tracking-wider gap-2"
          size="lg"
        >
          <LogIn className="h-4 w-4" />
          SIGN IN TO FORGE
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Trainer: Viewing As Banner — shown when trainer is in client context-switch mode */}
      {isViewingClient && activeClient && (
        <div className="sticky top-0 z-50 bg-amber-500/15 border-b border-amber-500/40 backdrop-blur-md" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="container py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-amber-400 shrink-0" />
              <span className="font-mono text-xs text-amber-300 uppercase tracking-wider">
                VIEWING AS: <span className="text-amber-100 font-bold">{activeClient.clientName.toUpperCase()}</span>
              </span>
            </div>
            <button
              onClick={exitClientView}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-200 transition-colors border border-amber-500/40 hover:border-amber-400 rounded px-2 py-1"
            >
              <X className="h-3 w-3" />
              EXIT CLIENT VIEW
            </button>
          </div>
        </div>
      )}

      {/* Session Recovery Banner — shown when a previous save failed */}
      {backedUpSessions.length > 0 && backedUpSessions.map(s => (
        <div key={s.id} className="sticky top-0 z-50 bg-orange-500/15 border-b border-orange-500/40 backdrop-blur-md">
          <div className="container py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="h-4 w-4 text-orange-400 shrink-0" />
              <span className="font-mono text-xs text-orange-300 uppercase tracking-wider truncate">
                UNSAVED SESSION: <span className="text-orange-100 font-bold">{s.name.toUpperCase()}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => handleRetryBackedUpSession(s)}
                disabled={retryingSave}
                className="font-mono text-[10px] uppercase tracking-wider text-orange-400 hover:text-orange-200 transition-colors border border-orange-500/40 hover:border-orange-400 rounded px-2 py-1 disabled:opacity-50"
              >
                {retryingSave ? 'SAVING...' : 'RETRY SAVE'}
              </button>
              <button
                type="button"
                onClick={() => handleDismissBackup(s.id)}
                className="font-mono text-[10px] uppercase tracking-wider text-orange-400/60 hover:text-orange-300 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Header Bar */}
      <header className="border-b border-border bg-card/60 backdrop-blur-md sticky top-0 z-40" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="container py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-primary flex items-center justify-center rounded-sm rotate-3">
              <Hammer className="h-5 w-5 text-primary-foreground -rotate-3" />
            </div>
            <div>
              <h1 className="font-mono text-base font-bold tracking-wider text-primary">FORGE // TELEMETRY</h1>
              <p className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest">
                {user?.name ? `${user.name.toUpperCase()}'S WORKOUT COMMAND CENTER` : "WORKOUT COMMAND CENTER"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isPaid && (
              <Link href="/pricing">
                <Button
                  variant="ghost"
                  size="sm"
                  title="Upgrade to Founding Member"
                  className="h-8 px-2 font-mono text-[10px] uppercase tracking-wider text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30"
                >
                  <Crown className="h-3.5 w-3.5 mr-1" />
                  Upgrade
                </Button>
              </Link>
            )}
            <Link href="/cardio">
              <Button
                variant="ghost"
                size="icon"
                title="Cardio Log"
                className="h-8 w-8 text-muted-foreground hover:text-red-400"
              >
                <Activity className="h-4 w-4" />
              </Button>
            </Link>
            <Link href="/import">
              <Button
                variant="ghost"
                size="icon"
                title="Import Data"
                className="h-8 w-8 text-muted-foreground hover:text-primary"
              >
                <Upload className="h-4 w-4" />
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              title="Share Forge // Telemetry"
              className="h-8 w-8 text-muted-foreground hover:text-emerald-400"
              onClick={async () => {
                const shareData = {
                  title: 'Forge // Telemetry',
                  text: 'Check out what I\'m training with 🔥',
                  url: 'https://trainwithtelemetry.com',
                };
                if (navigator.share && navigator.canShare?.(shareData)) {
                  try {
                    await navigator.share(shareData);
                  } catch (err) {
                    // User cancelled — no toast needed
                    if ((err as Error).name !== 'AbortError') {
                      console.error('[Share] Error:', err);
                    }
                  }
                } else {
                  try {
                    await navigator.clipboard.writeText('https://trainwithtelemetry.com');
                    toast.success('Link copied to clipboard', { description: 'trainwithtelemetry.com' });
                  } catch {
                    toast.error('Share failed', { description: 'Visit trainwithtelemetry.com' });
                  }
                }
              }}
            >
              <Share2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExport}
              title="Export Backup"
              className="h-8 w-8 text-muted-foreground hover:text-primary"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Workspace */}
      <main className="flex-1 container py-4 md:py-6 pb-24 md:pb-6">
        {liveSession ? (
          <LiveSessionTracker
            session={liveSession as any}
            onComplete={handleLiveComplete}
            onCancel={handleLiveCancel}
          />
        ) : (
          <div className="space-y-6">
            {/* Desktop View Layout — full-width tabs including Calendar */}
            <div className="hidden md:block">
                <Tabs
                  value={activeTab === 'calendar' ? 'calendar' : activeTab}
                  onValueChange={(val) => setActiveTab(val as any)}
                  className="w-full"
                >
                  <TabsList className="bg-secondary border border-border p-1 gap-1 flex justify-start">
                    <TabsTrigger value="calendar" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                      <Crosshair className="h-3.5 w-3.5" /> COMMAND
                    </TabsTrigger>
                    <TabsTrigger value="plan" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                      <Hammer className="h-3.5 w-3.5" /> FORGE
                    </TabsTrigger>
                    <TabsTrigger value="weight" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                      <PersonStanding className="h-3.5 w-3.5" /> FORM
                    </TabsTrigger>
                    <TabsTrigger value="prs" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                      <Activity className="h-3.5 w-3.5" /> TELEMETRY
                    </TabsTrigger>
                    <TabsTrigger value="mobility" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                      <Gauge className="h-3.5 w-3.5" /> RECOVER
                    </TabsTrigger>
                    <TabsTrigger
                      value="profile"
                      onClick={(e) => { if (isViewingClient) { e.preventDefault(); setActiveTab('calendar'); } }}
                      className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5"
                    >
                      <LayoutGrid className="h-3.5 w-3.5" /> LOADOUT
                    </TabsTrigger>
                    <TabsTrigger value="fuel" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                      <Flame className="h-3.5 w-3.5" /> FUEL
                    </TabsTrigger>
                    {(user?.role === 'trainer' || user?.role === 'admin') && !isViewingClient && (
                      <TabsTrigger value="clients" className="font-mono text-xs px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" /> FLOOR
                      </TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent value="calendar" className="mt-4">
                    <div className="space-y-4">
                      <TelemetryInsightBannerWrapper />
                      <RecoveryStatusWidget />
                      <WorkoutCalendar
                        selectedDate={selectedDate}
                        onSelectDate={(date) => {
                          setSelectedDate(date);
                          setForceNewSession(false);
                          setActiveTab('plan');
                        }}
                        onAddNewWorkout={handleAddNewWorkout}
                        refreshTrigger={0}
                        fullWidth
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="plan" className="mt-4">
                    <div className="mb-3">
                      <TelemetryInsightBannerWrapper compact />
                    </div>
                    <WorkoutBuilder
                      selectedDate={selectedDate}
                      onSave={() => { setForceNewSession(false); triggerRefresh(); }}
                      onStartLive={handleStartLive as any}
                      forceNewSession={forceNewSession}
                      onForceNewSessionConsumed={() => setForceNewSession(false)}
                    />
                  </TabsContent>

                  <TabsContent value="weight" className="mt-4 space-y-6">
                    <WeightTracker
                      selectedDate={selectedDate}
                      onWeightChange={triggerRefresh}
                      refreshTrigger={0}
                      clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                    />
                    <BodyMeasurementsTracker
                      selectedDate={selectedDate}
                      refreshTrigger={0}
                      onMeasurementChange={triggerRefresh}
                      clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                    />
                  </TabsContent>

                  <TabsContent value="prs" className="mt-4">
                    <PRsAndTemplates
                      refreshTrigger={0}
                      clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                    />
                  </TabsContent>

                  <TabsContent value="mobility" className="mt-4">
                    {isViewingClient && activeClient ? (
                      <StretchingTracker
                        selectedDate={selectedDate}
                        clientId={activeClient.clientId}
                      />
                    ) : (
                      <RecoverTab />
                    )}
                  </TabsContent>

                  <TabsContent value="profile" className="mt-4">
                    <UserProfile />
                  </TabsContent>
                  <TabsContent value="fuel" className="mt-4">
                    <NutritionTracker
                      selectedDate={selectedDate}
                      clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                    />
                  </TabsContent>
                  <TabsContent value="clients" className="mt-4">
                    {isViewingClient ? <TrainerDashboard /> : <TrainerRoster />}
                  </TabsContent>
                </Tabs>
            </div>

            {/* Mobile View Layout */}
            <div className="block md:hidden space-y-4">
              {activeTab === 'calendar' && (
                <div className="space-y-4">
                  <TelemetryInsightBannerWrapper />
                  <RecoveryStatusWidget />
                  <WorkoutCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setForceNewSession(false);
                      setActiveTab('plan');
                    }}
                    onAddNewWorkout={handleAddNewWorkout}
                    refreshTrigger={0}
                  />
                </div>
              )}

              {activeTab === 'plan' && (
                <div className="space-y-3">
                  <TelemetryInsightBannerWrapper compact />
                  <WorkoutBuilder
                    selectedDate={selectedDate}
                    onSave={() => { setForceNewSession(false); triggerRefresh(); }}
                    onStartLive={handleStartLive as any}
                    forceNewSession={forceNewSession}
                    onForceNewSessionConsumed={() => setForceNewSession(false)}
                  />
                </div>
              )}

              {activeTab === 'weight' && (
                <div className="space-y-6">
                  <WeightTracker
                    selectedDate={selectedDate}
                    onWeightChange={triggerRefresh}
                    refreshTrigger={0}
                    clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                  />
                  <BodyMeasurementsTracker
                    selectedDate={selectedDate}
                    refreshTrigger={0}
                    onMeasurementChange={triggerRefresh}
                    clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                  />
                </div>
              )}

              {activeTab === 'prs' && (
                <PRsAndTemplates
                  refreshTrigger={0}
                  clientId={isViewingClient && activeClient ? activeClient.clientId : undefined}
                />
              )}

              {activeTab === 'mobility' && (
                isViewingClient && activeClient ? (
                  <StretchingTracker
                    selectedDate={selectedDate}
                    clientId={activeClient.clientId}
                  />
                ) : (
                  <RecoverTab />
                )
              )}

              {activeTab === 'profile' && <UserProfile />}
              {activeTab === 'fuel' && <NutritionTracker selectedDate={selectedDate} clientId={isViewingClient && activeClient ? activeClient.clientId : undefined} />}
              {activeTab === 'clients' && (isViewingClient ? <TrainerDashboard /> : <TrainerRoster />)}
            </div>
          </div>
        )}
      </main>

      {/* Sticky Mobile Bottom Navigation Bar */}
      {!liveSession && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/90 backdrop-blur-lg border-t border-border/80 px-2 py-1 flex justify-around items-center">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'calendar' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <Crosshair className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Command</span>
          </button>

          <button
            onClick={() => setActiveTab('plan')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'plan' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <Hammer className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Forge</span>
          </button>

          <button
            onClick={() => setActiveTab('weight')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'weight' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <PersonStanding className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Form</span>
          </button>

          <button
            onClick={() => setActiveTab('prs')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'prs' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <Activity className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Telemetry</span>
          </button>

          <button
            onClick={() => setActiveTab('mobility')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'mobility' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <Gauge className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Recover</span>
          </button>

          <button
            onClick={() => setActiveTab('fuel')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'fuel' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <Flame className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Fuel</span>
          </button>
          <button
            onClick={() => isViewingClient ? setActiveTab('calendar') : setActiveTab('profile')}
            className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'profile' ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <LayoutGrid className="h-5 w-5" />
            <span className="text-[9px] font-mono uppercase tracking-wider">Loadout</span>
          </button>
          {(user?.role === 'trainer' || user?.role === 'admin') && !isViewingClient && (
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex flex-col items-center gap-1 py-1 px-3 rounded-sm transition-colors ${activeTab === 'clients' ? 'text-primary' : 'text-muted-foreground'}`}
            >
              <Users className="h-5 w-5" />
              <span className="text-[9px] font-mono uppercase tracking-wider">Floor</span>
            </button>
          )}
        </div>
      )}

      {/* Footer (Desktop Only) */}
      <footer className="hidden md:block border-t border-border py-4 bg-card/20 mt-12">
        <div className="container flex flex-col sm:flex-row justify-between items-center text-[10px] font-mono text-muted-foreground uppercase tracking-wider gap-2">
          <span>FORGE TELEMETRY V2.0.0 // CLOUD SYNC</span>
          {!isPaid && (
            <Link href="/pricing" className="text-emerald-500 hover:text-emerald-400 transition-colors">
              ★ UPGRADE → FOUNDING MEMBER
            </Link>
          )}
          <span>STAY HEAVY • RIDE SAFE</span>
        </div>
      </footer>
    </div>
  );
}
